import iso4217Data from "./iso4217_currency_decimals.json";

/**
 * Map of currency codes to their minor unit exponent (number of decimal places).
 * Loaded from ISO 4217 standard data (iso4217_currency_decimals.json).
 * Default is 2.
 */
const CURRENCY_DECIMALS: Record<string, number> = Object.fromEntries(
	Object.entries(iso4217Data).filter(([key]) => !key.startsWith("_")),
) as Record<string, number>;

/**
 * Formats a monetary amount given in the smallest unit (e.g., cents) to a localized string.
 *
 * @param amount - The amount in the smallest currency unit (e.g., 1000 for $10.00).
 * @param currency - The ISO 4217 currency code (e.g., "USD").
 * @param locale - The locale to use for formatting (default: "en-US").
 * @returns The formatted currency string.
 */
export const formatCurrency = (
	amount: number | null | undefined,
	currency: string | null | undefined,
	locale = "en-US",
): string => {
	if (amount === null || amount === undefined) {
		return "--";
	}

	if (!currency) {
		return amount.toString();
	}

	const decimals = CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
	const value = amount / 10 ** decimals;

	try {
		const formattedNumber = new Intl.NumberFormat(locale, {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(value);
		return `${formattedNumber} ${currency.toUpperCase()}`;
	} catch (_error) {
		return `${value} ${currency}`;
	}
};
